/**
 * 🔗 PROCESS BLUEPRINT TYPES
 * 
 * Entidade intermediária que orquestra a relação bidirecional
 * entre Mapas VSM e Workflow Templates V9.
 * 
 * Relacionamento:
 *   process_blueprints
 *     ↳ maps (via source_id quando source_type = 'map')
 *     ↳ workflow_templates_v9 (via generated_template_id)
 * 
 * Sem dependência circular. Multi-tenant seguro.
 */

// ============================================================================
// ENUMS E TIPOS BASE
// ============================================================================

/** Tipo de origem do blueprint */
export type BlueprintSourceType = 'map' | 'workflow' | 'manual';

/** Status do blueprint */
export type BlueprintStatus = 'draft' | 'published' | 'archived' | 'standard';

/** Tipo de incremento semântico da versão */
export type VersionBumpType = 'major' | 'minor' | 'patch';

/** Categoria do blueprint (para controle de padrão por org) */
export type BlueprintCategory =
  | 'geral'
  | 'onboarding'
  | 'vendas'
  | 'producao'
  | 'qualidade'
  | 'logistica'
  | 'financeiro'
  | 'rh'
  | 'ti'
  | 'atendimento'
  | string;

/** Transições válidas de status */
export const VALID_STATUS_TRANSITIONS: Record<BlueprintStatus, BlueprintStatus[]> = {
  draft: ['published', 'archived'],
  published: ['standard', 'archived', 'draft'],
  standard: ['published', 'archived'],
  archived: ['draft'],
};

/** Labels legíveis para categorias pré-definidas */
export const CATEGORY_LABELS: Record<string, string> = {
  geral: 'Geral',
  onboarding: 'Onboarding',
  vendas: 'Vendas',
  producao: 'Produção',
  qualidade: 'Qualidade',
  logistica: 'Logística',
  financeiro: 'Financeiro',
  rh: 'Recursos Humanos',
  ti: 'Tecnologia da Informação',
  atendimento: 'Atendimento ao Cliente',
};

// ============================================================================
// ENTIDADES
// ============================================================================

/**
 * Item de checklist de uma etapa
 */
export interface BlueprintChecklistItem {
  /** ID único do item */
  id: string;
  /** Texto do item de checklist */
  label: string;
  /** Se é obrigatório para avançar a etapa */
  required?: boolean;
}

/**
 * Campo de formulário de uma etapa
 */
export interface BlueprintFormField {
  /** ID único do campo */
  id: string;
  /** Label exibido */
  label: string;
  /** Tipo do campo */
  type: 'text' | 'number' | 'date' | 'select' | 'checkbox' | 'textarea' | 'file' | 'currency';
  /** Se é obrigatório */
  required?: boolean;
  /** Opções para campos do tipo 'select' */
  options?: string[];
  /** Valor padrão */
  defaultValue?: string | number | boolean;
  /** Regras de validação */
  validation?: { min?: number; max?: number; pattern?: string; patternMessage?: string };
  /** Regra de visibilidade condicional */
  visibilityRule?: { dependsOnFieldId: string; operator: string; value?: string | number | boolean };
}

/**
 * Regras de transição de uma etapa
 */
export interface BlueprintStageRules {
  /** Se requer aprovação para avançar */
  requireApproval?: boolean;
  /** Se pode ser pulada */
  canSkip?: boolean;
  /** Condição customizada (expressão textual) */
  condition?: string;
  /** Se a etapa pode ser revertida */
  canRevert?: boolean;
}

/**
 * Configuração de notificações de uma etapa
 */
export interface BlueprintStageNotifications {
  /** Notificar ao iniciar etapa */
  onStart?: boolean;
  /** Notificar ao completar etapa */
  onComplete?: boolean;
  /** Notificar quando SLA atingir X% do limite */
  onSlaWarningPercent?: number;
  /** Notificar quando SLA estourar */
  onSlaBreached?: boolean;
}

/**
 * Opções de saída/evidência de uma etapa
 */
export interface BlueprintExitOptions {
  /** Exige anexo obrigatório */
  requireAttachment?: boolean;
  /** Exige observação/justificativa */
  requireJustification?: boolean;
  /** Exige foto (mobile) */
  requirePhoto?: boolean;
  /** Configuração avançada de anexos */
  attachmentConfig?: { multiple?: boolean; allowedTypes?: string[]; maxSizeMB?: number };
}

/**
 * Etapa extraída do blueprint
 * Representa uma etapa do processo em formato intermediário
 */
export interface BlueprintStage {
  /** Ordem sequencial da etapa (1, 2, 3...) */
  order: number;
  /** Nome da etapa */
  name: string;
  /** Descrição opcional */
  description?: string;
  /** SLA em horas (opcional) */
  slaHours?: number;
  /** Role ou ID do responsável pela etapa */
  role?: string;
  /** Checklist de verificação da etapa */
  checklist?: BlueprintChecklistItem[];
  /** Campos de formulário da etapa */
  formFields?: BlueprintFormField[];
  /** Regras de transição */
  rules?: BlueprintStageRules;
  /** Configuração de notificações */
  notifications?: BlueprintStageNotifications;
  /** Metadados livres da etapa */
  metadata?: Record<string, unknown>;
  /** ID do card/nó original (para rastreabilidade) */
  sourceNodeId?: string;
  /** Instrução de trabalho / SOP (Standard Operating Procedure) */
  instruction?: string;
  /** Tipo de conclusão da etapa pelo operador */
  completionType?: 'simple_complete' | 'approve_reject';
  /** Opções de saída/evidência */
  exitOptions?: BlueprintExitOptions;
  tpStandard?: number;
  traStandard?: number;
  expectedErrorPercent?: number;
  correctionTimeStandard?: number;
  warningThresholdPercent?: number;
  wipLimit?: number;
  requireApproval?: boolean;
  /** Configuração de execução completa (blocks, SLA actions, etc.) */
  executionConfig?: Record<string, unknown>;
  /** ID do departamento (FK para org_departments) */
  departmentId?: string;
}

/**
 * Dados de mapa gerado a partir de um blueprint
 * Usado quando workflow → mapa
 */
export interface GeneratedMapData {
  /** Cards do mapa VSM gerados */
  cards: Array<{
    id: string;
    nome_fluxo?: string;
    atividade: string;
    posicao: { x: number; y: number };
    tipo: string;
    tp?: number;
    lt?: number;
    tt?: number;
    entrada?: number;
    sector?: string;
    responsavel?: string;
    pessoasSimultaneas?: number;
    traUnit?: string;
  }>;
  /** Conexões sequenciais entre os cards */
  connections: Array<{
    sourceId: string;
    targetId: string;
  }>;
  /** Flag indicando que foi gerado a partir de workflow */
  generatedFromWorkflow: true;
}

/**
 * Process Blueprint - Entidade Intermediária Mapa ↔ Workflow
 * 
 * Nunca vincula mapa diretamente a workflow.
 * Sempre usa esta entidade como intermediária.
 */
export interface ProcessBlueprint {
  /** UUID primary key */
  id: string;
  /** ID da organização (multi-tenant) */
  organizationId: string;
  /** ID do projeto (opcional - NULL se blueprint for global) */
  projectId?: string | null;
  /** Escopo de visibilidade */
  visibilityScope?: 'private' | 'project' | 'organization';
  /** ID do proprietário */
  ownerId?: string | null;

  // Identificação
  /** Nome do blueprint */
  name: string;
  /** Descrição do blueprint */
  description?: string;

  // Origem
  /** Tipo de origem: 'map', 'workflow' ou 'manual' */
  sourceType: BlueprintSourceType;
  /** ID do mapa ou workflow_template que originou (null se manual) */
  sourceId?: string | null;

  // Dados do blueprint
  /** Sequência de etapas extraída da origem */
  stages: BlueprintStage[];

  // Referências geradas
  /** ID do workflow_template_v9 gerado (se houver) */
  generatedTemplateId?: string | null;
  /** Dados do mapa gerado (se houver) */
  generatedMapData?: GeneratedMapData | null;

  // Status
  /** Status do blueprint */
  status: BlueprintStatus;
  /** Versão semântica completa (ex: 1.0.0) */
  versionSemver: string;
  /** Componentes da versão semântica */
  versionMajor: number;
  versionMinor: number;
  versionPatch: number;

  // Governança / Excelência
  /** Categoria do processo (ex: onboarding, vendas, producao) */
  category: BlueprintCategory;
  /** ID do blueprint pai (cadeia de versionamento) */
  parentBlueprintId?: string | null;
  /** Flag de padrão corporativo (derivada de status === 'standard') */
  isCorporateStandard: boolean;

  // Auditoria
  /** ID do usuário que criou */
  createdBy?: string;
  /** Nome do usuário que criou (resolvido em runtime) */
  createdByName?: string;
  /** Data de criação */
  createdAt: string;
  /** Data de última atualização */
  updatedAt: string;
}

// ============================================================================
// DTOs (Data Transfer Objects)
// ============================================================================

/**
 * Payload para criar um novo blueprint
 */
export interface CreateBlueprintPayload {
  organizationId: string;
  projectId?: string | null;
  name: string;
  description?: string;
  sourceType: BlueprintSourceType;
  sourceId?: string | null;
  stages: BlueprintStage[];
  status?: BlueprintStatus;
  category?: BlueprintCategory;
  createdBy?: string;
  /** Semver inicial utilizado para criar o primeiro registro em process_blueprint_versions */
  versionSemver?: string;
  versionMajor?: number;
  versionMinor?: number;
  versionPatch?: number;
  parentBlueprintId?: string | null;
  isCorporateStandard?: boolean;
}

/**
 * Payload para atualizar um blueprint
 */
export interface UpdateBlueprintPayload {
  name?: string;
  description?: string;
  stages?: BlueprintStage[];
  status?: BlueprintStatus;
  category?: BlueprintCategory;
  generatedTemplateId?: string | null;
  generatedMapData?: GeneratedMapData | null;
  isCorporateStandard?: boolean;
}

/**
 * Resultado da geração de pré-workflow a partir de mapa
 */
export interface MapToWorkflowResult {
  blueprint: ProcessBlueprint;
  templateId: string;
  stagesCreated: number;
}

/**
 * Resultado da geração de mapa a partir de workflow
 */
export interface WorkflowToMapResult {
  blueprint: ProcessBlueprint;
  mapData: GeneratedMapData;
}

/**
 * KPIs do módulo Excelência
 */
export interface ExcelenciaKPIs {
  /** Total de blueprints na organização */
  totalBlueprints: number;
  /** Blueprints publicados */
  published: number;
  /** Blueprints em rascunho */
  drafts: number;
  /** Blueprints com status 'standard' */
  standards: number;
  /** Blueprints marcados como padrão corporativo */
  corporateStandards: number;
  /** Originados de mapas VSM */
  fromMaps: number;
  /** Originados de workflows */
  fromWorkflows: number;
  /** Última atualização (ISO date string) */
  lastUpdated: string | null;
  /** Blueprints usados em pelo menos 1 projeto (join usage table) */
  usedInProjects: number;
}

// ============================================================================
// VERSIONAMENTO
// ============================================================================

/**
 * Snapshot imutável de uma versão do blueprint.
 * Gravado automaticamente ao criar nova versão.
 */
export interface ProcessBlueprintVersion {
  /** UUID primary key */
  id: string;
  /** ID do blueprint raiz */
  blueprintId: string;
  /** ID da organização (multi-tenant) */
  organizationId: string;
  /** Número da versão (1, 2, 3...) */
  version: number;
  /** Versão semântica (ex: 1.0.0) */
  versionSemver: string;
  /** Componentes individuais da versão */
  versionMajor: number;
  versionMinor: number;
  versionPatch: number;
  /** Tipo de alteração aplicado (major/minor/patch) */
  changeType: VersionBumpType;
  /** Estado completo do blueprint no momento da versão */
  snapshotJson: Record<string, unknown>;
  /** SHA-256 hex-digest do snapshot_json para verificação de imutabilidade */
  snapshotHash?: string;
  /** Resumo opcional das alterações */
  changeSummary?: string;
  /** ID do usuário que criou a versão */
  createdBy?: string;
  /** Data de criação da versão */
  createdAt: string;
}

/**
 * Payload para criar uma versão snapshot
 */
export interface CreateVersionPayload {
  blueprintId: string;
  organizationId: string;
  version: number;
  versionSemver: string;
  versionMajor: number;
  versionMinor: number;
  versionPatch: number;
  changeType: VersionBumpType;
  snapshotJson: Record<string, unknown>;
  snapshotHash?: string;
  changeSummary?: string;
  createdBy?: string;
}

// ============================================================================
// RASTREIO DE USO
// ============================================================================

/**
 * Registro de uso de um blueprint em um projeto.
 */
export interface ProcessBlueprintUsage {
  /** UUID primary key */
  id: string;
  /** ID do blueprint sendo usado */
  blueprintId: string;
  /** ID do projeto que usa o blueprint */
  projectId: string;
  /** ID da organização (multi-tenant) */
  organizationId: string;
  /** ID do usuário que vinculou */
  linkedBy?: string;
  /** Data/hora do vínculo */
  linkedAt: string;
  /** Observação opcional */
  notes?: string;
  /** Versão numérica registrada no momento do vínculo */
  blueprintVersion: number;
  /** Versão semântica registrada no momento do vínculo */
  blueprintSemver: string;
  /** Status registrado no momento do vínculo */
  statusAtLink: BlueprintStatus;
  /** Categoria registrada no momento do vínculo */
  categoryAtLink: BlueprintCategory;
  /** Snapshot imutável do blueprint aplicado ao projeto */
  snapshotJson: Record<string, unknown>;
}

/**
 * Payload para vincular um blueprint a um projeto
 */
export interface LinkBlueprintToProjectPayload {
  blueprintId: string;
  projectId: string;
  organizationId: string;
  linkedBy?: string;
  notes?: string;
}

/**
 * Contagem de uso agregada (da view)
 */
export interface BlueprintUsageCount {
  blueprintId: string;
  organizationId: string;
  name: string;
  status: BlueprintStatus;
  category: BlueprintCategory;
  version: number;
  versionSemver: string;
  isCorporateStandard: boolean;
  usageCount: number;
  outdatedUsage: number;
  projectsUsing: number;
  versionCount: number;
}

// ============================================================================
// LOG DE GOVERNANÇA
// ============================================================================

/** Ações rastreadas no log de governança */
export type BlueprintLogAction =
  | 'created'
  | 'published'
  | 'versioned'
  | 'set_standard'
  | 'removed_standard'
  | 'archived'
  | 'reactivated'
  | 'edited'
  | 'cloned'
  | 'instance_created'
  | 'usage_linked'
  | 'usage_unlinked'
  | 'BLUEPRINT_QUALITY_ALERT';

/**
 * Registro de log de governança de um blueprint.
 */
export interface ProcessBlueprintLog {
  /** UUID primary key */
  id: string;
  /** ID do blueprint */
  blueprintId: string;
  /** ID da organização (multi-tenant) */
  organizationId: string;
  /** Ação realizada */
  action: BlueprintLogAction;
  /** ID do usuário que executou a ação */
  performedBy?: string;
  /** Detalhes extras (JSON) */
  details: Record<string, unknown>;
  /** Data/hora da ação */
  createdAt: string;
}

/** Métricas de impacto organizacional do módulo Excelência */
export interface GovernanceImpactMetrics {
  totalBlueprints: number;
  published: number;
  drafts: number;
  standards: number;
  outdatedUsages: number;
  totalUsages: number;
  projectsUsing: number;
  percentProjectsStandard: number;
}
